import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import StatusPill from '../components/StatusPill';
import './Security.css';

function repoStatus(repo) {
  if (repo.github_missing || repo.forge_init_pr_state === 'closed') return ['red', 'needs attention'];
  if (repo.rules_applied) return ['green', 'rules active'];
  if (repo.forge_init_pr_state === 'merged') return ['amber', 'rules pending'];
  return ['amber', 'governance pending'];
}

function Security() {
  const { apiUrl, userId } = useOutletContext();
  const [searchParams] = useSearchParams();
  const [repos, setRepos] = useState([]);
  const [redCases, setRedCases] = useState([]);
  const [redCasesError, setRedCasesError] = useState(null);
  const [environmentData, setEnvironmentData] = useState(null);
  const [environmentError, setEnvironmentError] = useState(null);
  const [selectedRepoId, setSelectedRepoId] = useState(searchParams.get('repo_id') || '');
  const [selectedEnvironment, setSelectedEnvironment] = useState('prod');
  const [target, setTarget] = useState({ aws_account_id: '', aws_role_arn: '', aws_region: 'us-east-1' });
  const [secretValues, setSecretValues] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!userId) return;
    fetch(`${apiUrl}/api/repos/?user_id=${userId}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setRepos(Array.isArray(data) ? data : []))
      .catch(() => setRepos([]));

    fetch(`${apiUrl}/api/audit-log/red-cases?user_id=${userId}`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'No se pudieron cargar los casos rojos.');
        setRedCases(data);
      })
      .catch((err) => setRedCasesError(err.message === 'Failed to fetch' ? 'Backend no disponible.' : err.message));
  }, [apiUrl, userId]);

  const activeEnvironment = useMemo(
    () => environmentData?.environments?.find((item) => item.name === selectedEnvironment),
    [environmentData, selectedEnvironment]
  );

  const internalSecrets = useMemo(
    () => (activeEnvironment?.secrets || []).filter((secret) => secret.kind === 'internal'),
    [activeEnvironment]
  );

  const internalSecretsReady = internalSecrets.length > 0
    && internalSecrets.every((secret) => secret.status === 'configured');

  const loadEnvironments = async (repositoryId = selectedRepoId) => {
    if (!repositoryId || !userId) {
      setEnvironmentData(null);
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/api/environments/${repositoryId}?user_id=${userId}`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudieron cargar los entornos.');
      setEnvironmentData(data);
      setEnvironmentError(null);
    } catch (err) {
      setEnvironmentData(null);
      setEnvironmentError(err.message === 'Failed to fetch' ? 'Backend no disponible.' : err.message);
    }
  };

  useEffect(() => {
    loadEnvironments();
  }, [apiUrl, selectedRepoId, userId]);

  const run = async (operation) => {
    setBusy(true);
    try {
      await operation();
      await loadEnvironments();
      setSecretValues({});
    } catch (err) {
      setEnvironmentError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const request = async (path, options = {}) => {
    const response = await fetch(`${apiUrl}${path}?user_id=${userId}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      ...options,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'La operación no se pudo completar.');
    return data;
  };

  const configureTarget = () => run(() => request(`/api/environments/${selectedRepoId}/${selectedEnvironment}`, {
    method: 'PUT', body: JSON.stringify(target),
  }));

  const setSecret = (key) => run(() => request(`/api/environments/${selectedRepoId}/${selectedEnvironment}/secrets/${key}`, {
    method: 'PUT', body: JSON.stringify({ value: secretValues[key] || '' }),
  }));

  return (
    <div className="forge-page">
      <div className="forge-page-header">
        <div>
          <div className="forge-section-eyebrow">Módulo 05 · Seguridad</div>
          <h1 className="forge-title">Asegurar</h1>
          <p className="forge-lead">
            Barreras operativas para que la seguridad no dependa de un prompt:
            governance PR, branch protection, adapters, red cases y auditoría.
          </p>
        </div>
      </div>

      <div className="forge-grid three">
        <div className="forge-panel">
          <span className="forge-kbd">01</span>
          <h2>Política fuera del prompt</h2>
          <p>La autorización vive en reglas, adapters y checks verificables.</p>
        </div>
        <div className="forge-panel">
          <span className="forge-kbd">02</span>
          <h2>Adapters, no APIs crudas</h2>
          <p>Los agentes usan herramientas explícitas con contratos y auditoría.</p>
        </div>
        <div className="forge-panel">
          <span className="forge-kbd">03</span>
          <h2>Evaluación desacoplada</h2>
          <p>El agente no decide si su propio output es seguro o suficiente.</p>
        </div>
      </div>

      <h2 className="forge-section-title"><span className="num">A.</span> Casos rojos</h2>
      <div className="forge-list">
        {redCasesError && <div className="message error">{redCasesError}</div>}
        {!redCasesError && redCases.map((cat) => (
          <div key={cat.key} className="forge-row">
            <div className="forge-row-main">
              <div className="forge-row-title">{cat.label}</div>
              <div className="forge-row-detail">
                {cat.has_runtime_detection
                  ? (cat.recent[0]
                      ? `Más reciente: ${cat.recent[0].repo_full_name} · ${new Date(cat.recent[0].created_at).toLocaleString()}`
                      : 'Monitoreado -- sin incidentes registrados')
                  : cat.note}
              </div>
            </div>
            {cat.has_runtime_detection ? (
              <StatusPill color={cat.count > 0 ? 'red' : 'green'}>
                {cat.count > 0 ? `${cat.count} incidente${cat.count === 1 ? '' : 's'}` : '0 incidentes'}
              </StatusPill>
            ) : (
              <StatusPill color="blue">bloqueado por diseño</StatusPill>
            )}
          </div>
        ))}
      </div>

      <h2 className="forge-section-title"><span className="num">B.</span> Controles por repo</h2>
      <div className="forge-list">
        {repos.map((repo) => {
          const [color, label] = repoStatus(repo);
          return (
            <div key={repo.id} className="forge-row">
              <div className="forge-row-main">
                <div className="forge-row-title">{repo.full_name}</div>
                <div className="forge-row-detail">{repo.rules_apply_note || 'Forge governance + repository rules status'}</div>
              </div>
              <StatusPill color={color}>{label}</StatusPill>
            </div>
          );
        })}
        {repos.length === 0 && <div className="forge-empty">No hay repositorios conectados para evaluar controles.</div>}
      </div>

      <section className="forge-secret-section" aria-labelledby="environment-secrets-title">
        <div className="forge-section-heading">
          <div>
            <div className="forge-section-eyebrow">SP-076 · Secretos por entorno</div>
            <h2 id="environment-secrets-title" className="forge-section-title"><span className="num">C.</span> Configurar destino AWS</h2>
            <p>FORGE guarda los valores en SSM como SecureString. Aquí solo se ven estados: nunca secretos.</p>
          </div>
          {environmentData && <StatusPill color={environmentData.can_manage ? 'green' : 'blue'}>{environmentData.can_manage ? 'puedes gestionar' : 'solo consulta'}</StatusPill>}
        </div>
        <div className="forge-inline-alert">
          <StatusPill color="blue">GitHub global</StatusPill>
          <span>El webhook de GitHub se administra una sola vez en Administrar. Estos secretos pertenecen únicamente al despliegue del proyecto.</span>
        </div>

        {environmentError && <div className="message error">{environmentError}</div>}
        <label className="forge-secret-field">Proyecto
          <select value={selectedRepoId} onChange={(event) => setSelectedRepoId(event.target.value)}>
            <option value="">Selecciona un proyecto</option>
            {repos.map((repo) => <option key={repo.id} value={repo.id}>{repo.full_name}</option>)}
          </select>
        </label>

        {selectedRepoId && <>
          <div className="forge-environment-tabs" role="tablist" aria-label="Entornos">
            {(environmentData?.environments || [{ name: 'dev' }, { name: 'staging' }, { name: 'prod' }]).map((environment) => (
              <button key={environment.name} type="button" role="tab" aria-selected={selectedEnvironment === environment.name}
                className={selectedEnvironment === environment.name ? 'active' : ''} onClick={() => setSelectedEnvironment(environment.name)}>
                {environment.name} {environment.configured ? '· configurado' : '· pendiente'}
              </button>
            ))}
          </div>

          {!activeEnvironment?.configured && environmentData?.can_manage && <div className="forge-environment-card">
            <h3>1. Conecta la cuenta destino</h3>
            <p>Usa un IAM Role cross-account del cliente. FORGE generará el ExternalId necesario para la política de confianza.</p>
            <div className="forge-target-grid">
              <label className="forge-secret-field">AWS account ID<input value={target.aws_account_id} maxLength="12" inputMode="numeric" onChange={(event) => setTarget({ ...target, aws_account_id: event.target.value })} placeholder="123456789012" /></label>
              <label className="forge-secret-field">Región<input value={target.aws_region} onChange={(event) => setTarget({ ...target, aws_region: event.target.value })} /></label>
              <label className="forge-secret-field forge-target-role">IAM Role ARN<input value={target.aws_role_arn} onChange={(event) => setTarget({ ...target, aws_role_arn: event.target.value })} placeholder="arn:aws:iam::123456789012:role/ForgeSSMRole" /></label>
            </div>
            <button className="btn-create" disabled={busy} onClick={configureTarget}>Guardar destino AWS</button>
          </div>}

          {!activeEnvironment?.configured && environmentData && !environmentData.can_manage && <div className="forge-empty">El destino AWS aún no está configurado. Solicita a un Forge Master o Admin que lo conecte.</div>}

          {activeEnvironment?.configured && <div className="forge-environment-card">
            <div className="forge-environment-summary">
              <div>
                <h3>Entorno {selectedEnvironment} conectado</h3>
                <p>Cuenta terminada en {activeEnvironment.account_suffix} · Región {activeEnvironment.aws_region}</p>
              </div>
              {environmentData.can_manage && (internalSecretsReady ? (
                <div className="forge-internal-secrets-ready" role="status">
                  <StatusPill color="green">Secretos internos listos</StatusPill>
                </div>
              ) : (
                <button className="btn-create" disabled={busy} onClick={() => run(() => request(`/api/environments/${selectedRepoId}/${selectedEnvironment}/initialize`, { method: 'POST' }))}>Generar secretos seguros</button>
              ))}
            </div>
            {environmentData.can_manage && activeEnvironment.external_id && <details className="forge-external-id">
              <summary>Configuración avanzada para IAM</summary>
              <div className="forge-external-id-content">
                <p>Usa este ExternalId en la política de confianza del rol del cliente.</p>
                <code>{activeEnvironment.external_id}</code>
              </div>
            </details>}

            <div className="forge-secret-list">
              {(activeEnvironment.secrets || []).map((secret) => <article className="forge-secret-row" key={secret.key}>
                <div><b>{secret.key}</b><small>{secret.kind === 'internal' ? 'Generado por FORGE' : 'Captura write-only'}</small></div>
                <StatusPill color={secret.status === 'configured' ? 'green' : secret.status === 'validation_failed' ? 'red' : 'amber'}>{secret.status === 'configured' ? 'Configurado' : secret.status === 'validation_failed' ? 'Validación fallida' : 'Pendiente'}</StatusPill>
                {environmentData.can_manage && <div className="forge-secret-actions">
                  {secret.kind === 'external' && <input type="password" autoComplete="new-password" value={secretValues[secret.key] || ''} onChange={(event) => setSecretValues({ ...secretValues, [secret.key]: event.target.value })} placeholder="Pegar valor una sola vez" aria-label={`Valor para ${secret.key}`} />}
                  {secret.kind === 'external' && <button disabled={busy || !(secretValues[secret.key] || '').trim()} onClick={() => setSecret(secret.key)}>Guardar</button>}
                  {secret.kind === 'internal' && <button disabled={busy} onClick={() => run(() => request(`/api/environments/${selectedRepoId}/${selectedEnvironment}/secrets/${secret.key}/rotate`, { method: 'POST' }))}>Rotar</button>}
                  {secret.status === 'configured' && <button disabled={busy} onClick={() => run(() => request(`/api/environments/${selectedRepoId}/${selectedEnvironment}/secrets/${secret.key}/validate`, { method: 'POST' }))}>Validar</button>}
                </div>}
              </article>)}
            </div>
          </div>}
        </>}
      </section>
    </div>
  );
}

export default Security;
